// utils/productAvailability.js
//
// The SINGLE reader for the two dated availability facts, shared verbatim by
// every surface that shows them: the post card (feed + bookmarks), the product
// sheet, the add-to-box editor, and a pending order-item row.
//
// THE CONTRACT (BE→FE `2026-08-15-oos-two-facts-availability-contract.md`).
// Out of stock stopped being a TIMER and became a pair of dated facts:
//
//   availability   { state:'oos', since:ISO, supplier?:string,
//                    wholeProduct:bool, variants:string[], note?:string }
//   lastCollected  { at:ISO, supplier?:string, price:int }
//
// The old `{ state:'unavailable', until:ISO }` is GONE. `until` was derived from
// an expiry column that no longer decides anything — nothing expires on a
// schedule; a mark stands until a real event clears it. Do not resurrect it, and
// do not write copy that promises a return date: the club does not know one.
//
// `since` is the LAST time it was marked, not the longest-standing mark. Its job
// on the member's screen is to let her judge how FRESH the information is, and
// `supplier` / `note` are read off that same mark — never mixed across rows.
//
// TWO RULES THIS MODULE ENFORCES SO NO CALLER HAS TO REMEMBER THEM:
//
//  1. ABSENCE IS AN OMITTED KEY, NEVER NULL — for the whole payload and for the
//     optional keys inside it (`supplier`, `note`). Callers branch on the return
//     value being null; they never inspect the raw attribute. Writing code that
//     distinguishes "absent" from "explicit null" is meaningless: the wire
//     cannot express the difference.
//  2. `state` IS READ, NOT ASSUMED. An unknown future state value returns null
//     rather than rendering a raw key at a member. This is the same discipline
//     that `walletKind` violated once, when an unlisted value made goodwill
//     refunds vanish from every wallet tab — except here the failure mode is
//     inverted (show garbage rather than hide truth), so the guard is a
//     whitelist of ONE with an honest null.
//
// Pure module — no Vue, no i18n, no F7. Callers own interpolation and styling.

/** The only `availability.state` this build knows how to render. */
export const AVAILABILITY_STATE_OOS = 'oos';

function trimmedOrNull(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** Parse an ISO-8601 instant, or null. A malformed date renders NOTHING rather
 *  than "Invalid Date" — every fact here is dated, so a fact we cannot date is
 *  not a fact we can show. */
function toDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * The «مش متوفر» hint for a post, product or order line — or null when there is
 * nothing to say.
 *
 * @param {object|null|undefined} resource Anything carrying an `availability`
 *   attribute: a Post, a Bookmark, a Product, an OrderBoxItem.
 * @returns {{since: Date, supplier: string|null, wholeProduct: boolean,
 *   variants: string[], variantIds: number[]|null, note: string|null}|null}
 */
export function availabilityHint(resource) {
  const raw = resource?.availability;
  if (raw == null || typeof raw !== 'object') return null;
  if (raw.state !== AVAILABILITY_STATE_OOS) return null;

  const since = toDate(raw.since);
  if (since === null) return null;

  return {
    since,
    supplier: trimmedOrNull(raw.supplier),
    // Any whole-product claim outranks the per-variant marks beside it: the
    // admin said "we can't get this at all".
    wholeProduct: raw.wholeProduct === true,
    // A variant soft-deleted since the mark was opened contributes no name, so
    // this list can legitimately be shorter than the set of affected variants —
    // and can be empty even when wholeProduct is false.
    variants: Array.isArray(raw.variants) ? raw.variants.map(trimmedOrNull).filter(Boolean) : [],
    // The SAME list, by identity (U-11). Index-aligned with `variants` by
    // construction — one backend pass over the same rows — so a caller may key
    // on the id and label from the name without checking the two lengths agree.
    //
    // NULL means the key was ABSENT: a payload predating the ids, where a name
    // comparison is the only thing available. An EMPTY ARRAY is a real
    // statement — "no per-variant mark resolves" — and must not fall back to
    // names. That distinction is the whole reason this is not `?? []`.
    variantIds: Array.isArray(raw.variantIds)
      ? raw.variantIds.map(Number).filter(Number.isFinite)
      : null,
    note: trimmedOrNull(raw.note),
  };
}

/**
 * When a post's live out-of-stock mark was last written, as a sortable number.
 * An unreadable mark is `-Infinity`: it sorts LAST on a descending read, which is
 * the opposite of what NULLs do on a descending database sort and the right way
 * round for a member — the list she asked for leads with real, dated marks.
 *
 * @param {object|null|undefined} post
 * @returns {number}
 */
export function markedAt(post) {
  return availabilityHint(post)?.since?.getTime() ?? -Infinity;
}

/**
 * How many OTHER members are waiting for this post — a positive whole number, or
 * 0. The backend OMITS the key at zero and on every read that does not carry it,
 * and the model hydrates an absent key to null, so only a positive integer counts
 * (BE→FE brief 2026-10-02-suggestion-reward).
 *
 * @param {object|null|undefined} post
 * @returns {number}
 */
export function waitingMembersOf(post) {
  const count = post?.waitingMembers;
  return Number.isInteger(count) && count > 0 ? count : 0;
}

/**
 * The «مش متوفر» feed's order, read back out of the ORM: the SAME order the
 * server answers `sort=-waitingMembers` in — most waiting members first, then the
 * newest out-of-stock mark, then the newest post (id, all descending). Used as an
 * `Array.prototype.sort` comparator.
 *
 * A post without a count sorts as zero waiting, which is also what the server
 * does with a post nobody holds a line on.
 */
export function compareWantedFirst(a, b) {
  const waiting = waitingMembersOf(b) - waitingMembersOf(a);
  if (waiting !== 0) return waiting;

  const mark = markedAt(b) - markedAt(a);
  // Two unreadable marks are -Infinity - -Infinity = NaN; that is a tie.
  if (mark !== 0 && !Number.isNaN(mark)) return mark;

  return (Number(b?.id) || 0) - (Number(a?.id) || 0);
}

/**
 * "The club last got this on `at`, from `supplier`, for `price`" — or null.
 *
 * `priceCents` is the gross UNIT price in piasters, not a line total. Poola
 * lines are excluded backend-side: a flash-sale deal price is not a repeatable
 * price, so it must never be shown as one.
 *
 * @param {object|null|undefined} resource
 * @returns {{at: Date, supplier: string|null, priceCents: number}|null}
 */
export function lastCollectedFact(resource) {
  const raw = resource?.lastCollected;
  if (raw == null || typeof raw !== 'object') return null;

  const at = toDate(raw.at);
  if (at === null) return null;

  const priceCents = Number(raw.price);
  if (!Number.isFinite(priceCents)) return null;

  return { at, supplier: trimmedOrNull(raw.supplier), priceCents };
}

/**
 * Does this hint cover the thing the member is about to put in her box?
 *
 * A whole-product claim covers everything. Otherwise it covers exactly the
 * variants the mark names.
 *
 * MATCHED BY ID (U-11, closed 2026-09-07). `availability` now carries
 * `variantIds` beside `variants`, built in ONE pass over the same rows and
 * index-aligned, so `variantIds[i]` identifies `variants[i]` and a variant that
 * no longer resolves to a name drops out of BOTH. That is what lets this ask an
 * identity question instead of comparing display text: two variants of one
 * product sharing a name used to BOTH match, and a variant renamed since the
 * mark was opened stopped matching altogether. The ids are deduped on the id
 * rather than the name — deliberately, since a name-keyed dedupe would report
 * one id and leave the other variant silently out.
 *
 * THE NAME PATH REMAINS, for exactly one case: a payload written before the ids
 * existed, which carries `variants` and no `variantIds` key at all. Branching on
 * key PRESENCE rather than emptiness is load-bearing — an empty `variantIds` on
 * a current payload is a real statement ("no per-variant marks resolve"), and
 * falling back to names there would answer a question the payload already
 * answered. Delete the fallback once no cached client can hold a pre-ids
 * payload.
 *
 * It never blocks: this drives a non-blocking warning, so the worst case has
 * always been a warning shown or missed, never an order refused.
 *
 * @param {ReturnType<typeof availabilityHint>} hint
 * @param {number|string|null|undefined} variantId The id of the variant she
 *   picked. Callers that genuinely have only a name pass it as the third
 *   argument instead.
 * @param {string|null|undefined} [variantName] Used ONLY on a pre-ids payload.
 * @returns {boolean}
 */
export function hintCoversVariant(hint, variantId, variantName = null) {
  if (!hint) return false;
  if (hint.wholeProduct) return true;

  if (hint.variantIds !== null) {
    const id = Number(variantId);
    return Number.isFinite(id) && hint.variantIds.includes(id);
  }

  const name = trimmedOrNull(variantName);
  if (name === null) return false;

  return hint.variants.includes(name);
}

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

// Western digits ALWAYS (owner-locked UI rule) — `-u-nu-latn` guarantees it
// regardless of the active app locale, the same way utils/poolaReportFormat.js
// does. Arabic month names, Latin numerals.
const DATE_LOCALE = {
  ar: 'ar-u-nu-latn',
  en: 'en-u-nu-latn',
};

/**
 * A short ABSOLUTE date: "12 أغسطس", or "12 أغسطس 2025" when the year differs
 * from the reading year.
 *
 * Absolute, not relative, on purpose. "3 days ago" re-reads itself into a
 * different claim every time the page is opened, and the whole job of this date
 * is to let the member judge how fresh the information is against her own sense
 * of when the club last shopped. The year appears only when it is load-bearing,
 * so the common case stays short enough for a dense list row.
 *
 * @param {Date|string|null|undefined} value
 * @param {string} [locale='ar']
 * @param {Date} [now] Injectable for tests; defaults to the current instant.
 * @returns {string} '' when the date is missing or unparseable — callers render
 *   nothing rather than a placeholder.
 */
export function formatAvailabilityDate(value, locale = 'ar', now = new Date()) {
  const date = toDate(value);
  if (date === null) return '';

  const intlLocale = DATE_LOCALE[locale] ?? DATE_LOCALE.ar;
  const options = { day: 'numeric', month: 'long' };
  if (date.getFullYear() !== now.getFullYear()) options.year = 'numeric';

  return new Intl.DateTimeFormat(intlLocale, options).format(date);
}
